/**
 * Tech-debt #4 — Device Model / configuration family: Reset, the v1.6
 * GetConfiguration / ChangeConfiguration pair, and the v2.0.1 Device Model
 * (Get/SetVariables, GetBaseReport, GetReport).
 */
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { variableToReportData } from '@/protocol/v201/actions/notifyReport';
import { requiresReboot } from '@/protocol/v201/deviceModel';
import { DEFAULT_CONFIGURATION_KEYS_V16 } from '@/protocol/v16/OcppV16Adapter';
import { useConfigurationStore } from '../useConfigurationStore';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';
import { configRepo, emitNotifyReports, findVar, variableExists, varValuesRepo } from './shared';
import { fireMonitorEvents } from './monitoring';

export function handleReset(event: CsmsEvent<'Reset'>, ctx: CsmsCtx): void {
  // BUG-024 / Session 8f.15 — actually reset per OCPP 1.6J §4.18. Reply
  // Accepted FIRST (synchronous), then schedule the real side effect on a
  // 50 ms tick so the CALLRESULT serializes onto the wire before we tear the
  // WS down. ResetStatus { Accepted, Rejected }; the simulator never refuses.
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
  const isHard = event.type === 'Hard';
  setTimeout(() => {
    const store = useStationStore.getState();
    if (isHard) {
      // Hard = power-cycle. Disconnect (releases lock + dispatcher), then
      // reload the SPA — the user re-arrives on the same URL, re-acquires the
      // lock, and re-fires BootNotification on the next Connect.
      void store.disconnect().finally(() => {
        if (typeof window !== 'undefined') window.location.reload();
      });
    } else {
      // Soft = clear in-tab state + open transactions, keep station identity +
      // saved profiles. `softReset` already disconnects first.
      void store.softReset();
    }
  }, 50);
}

export async function handleChangeConfiguration(
  event: CsmsEvent<'ChangeConfiguration'>,
  ctx: CsmsCtx,
): Promise<void> {
  // BUG-018 — actually persist the new value via useConfigurationStore so a
  // CSMS-driven ChangeConfiguration is visible to GetConfiguration AND to the
  // live MV emit interval in StationPage. ChangeConfigurationStatus
  // { Accepted, Rejected, RebootRequired, NotSupported }; we don't simulate
  // reboot so RebootRequired is never used.
  const existing = useConfigurationStore.getState().entries.find((e) => e.key === event.key);
  if (!existing) {
    const known = DEFAULT_CONFIGURATION_KEYS_V16.some((d) => d.key === event.key);
    if (!known) {
      ctx.dispatcher.reply(ctx.messageId, { status: 'NotSupported' });
      return;
    }
  }
  if (existing?.readonly === true) {
    ctx.dispatcher.reply(ctx.messageId, { status: 'Rejected' });
    return;
  }
  const ok = await useConfigurationStore.getState().setValue(ctx.stationId, event.key, event.value);
  ctx.dispatcher.reply(ctx.messageId, { status: ok ? 'Accepted' : 'Rejected' });
}

export async function handleGetConfiguration(
  event: CsmsEvent<'GetConfiguration'>,
  ctx: CsmsCtx,
): Promise<void> {
  const all = await configRepo.byStation(ctx.stationId);
  const filtered =
    event.keys && event.keys.length > 0 ? all.filter((k) => event.keys?.includes(k.key)) : all;
  const unknownKey = event.keys?.filter((k) => !all.some((row) => row.key === k)) ?? [];
  ctx.dispatcher.reply(ctx.messageId, {
    configurationKey: filtered.map((k) => ({ key: k.key, readonly: k.readonly, value: k.value })),
    ...(unknownKey.length > 0 ? { unknownKey } : {}),
  });
}

export async function handleGetVariables(
  event: CsmsEvent<'GetVariables'>,
  ctx: CsmsCtx,
): Promise<void> {
  // Session 19b — structured query against the Device Model (variableValues).
  // attributeType defaults to Actual. Three outcomes per OCPP 2.0.1
  // GetVariableStatusEnum: Accepted (the attribute exists), NotSupportedAttributeType
  // (the Variable exists but not on this attributeType), UnknownVariable (no
  // such Variable at all).
  const all = await varValuesRepo.byStation(ctx.stationId);
  const result = event.entries.map((e) => {
    const attr = e.attributeType ?? 'Actual';
    const row = findVar(all, e.component, e.variable, attr);
    const base = {
      component: { name: e.component },
      variable: { name: e.variable },
      ...(e.attributeType !== undefined ? { attributeType: e.attributeType } : {}),
    };
    if (row) {
      return { attributeStatus: 'Accepted', attributeValue: row.value, ...base };
    }
    if (variableExists(all, e.component, e.variable)) {
      return { attributeStatus: 'NotSupportedAttributeType', ...base };
    }
    return { attributeStatus: 'UnknownVariable', ...base };
  });
  ctx.dispatcher.reply(ctx.messageId, { getVariableResult: result });
}

export async function handleSetVariables(
  event: CsmsEvent<'SetVariables'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // Session 19b — write through the Device Model with mutability +
  // RebootRequired semantics.
  const all = await varValuesRepo.byStation(stationId);
  const setResult: Record<string, unknown>[] = [];
  let anyWrite = false;
  for (const e of event.entries) {
    const attr = e.attributeType ?? 'Actual';
    const row = findVar(all, e.component, e.variable, attr);
    const base = {
      component: { name: e.component },
      variable: { name: e.variable },
      ...(e.attributeType !== undefined ? { attributeType: e.attributeType } : {}),
    };
    if (!row) {
      // No row for the requested attributeType. If the Variable itself is known
      // (it has an Actual row), a non-Actual attribute (Target/MinSet/MaxSet) is
      // created on demand — these are CSMS-configurable bounds/targets, so the
      // Actual row's mutability does not gate them. A missing Actual attribute,
      // or an unknown Variable, stays UnknownVariable.
      const actual = findVar(all, e.component, e.variable, 'Actual');
      if (actual !== undefined && attr !== 'Actual') {
        await varValuesRepo.upsert(stationId, {
          ...actual,
          attributeType: attr,
          value: e.attributeValue,
          mutability: 'ReadWrite',
        });
        anyWrite = true;
        setResult.push({ attributeStatus: 'Accepted', ...base });
      } else {
        setResult.push({ attributeStatus: 'UnknownVariable', ...base });
      }
      continue;
    }
    if (row.mutability === 'ReadOnly') {
      setResult.push({ attributeStatus: 'Rejected', ...base });
      continue;
    }
    await varValuesRepo.upsert(stationId, { ...row, value: e.attributeValue });
    anyWrite = true;
    // Session 22 — fire NotifyEvent if a monitor on this variable is crossed by
    // the new value (uses the pre-write `row.value` as old). Monitors observe
    // the Actual value, so only fire on Actual writes.
    if (adapter instanceof OcppV201Adapter && attr === 'Actual') {
      void fireMonitorEvents(adapter, stationId, e.component, e.variable, row.value, e.attributeValue);
    }
    const status = requiresReboot(e.component, e.variable) ? 'RebootRequired' : 'Accepted';
    setResult.push({ attributeStatus: status, ...base });
  }
  dispatcher.reply(messageId, { setVariableResult: setResult });
  // Re-project the Device Model into the flat store so behaviour-driving
  // consumers (HeartbeatInterval timer, measurands, …) pick up the change.
  if (anyWrite) {
    await useConfigurationStore.getState().refresh(stationId, '2.0.1');
  }
}

export async function handleGetBaseReport(
  event: CsmsEvent<'GetBaseReport'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // Session 16/19b — v2.0.1 CSMS asks for the Device Model inventory. Reply
  // Accepted immediately, then fire paginated NotifyReport(s). reportBase:
  // SummaryInventory → ReadWrite variables only; otherwise everything.
  if (!(adapter instanceof OcppV201Adapter)) {
    dispatcher.replyError(messageId, 'NotImplemented', 'GetBaseReport is v2.0.1-only', {});
    return;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
  const rows = await varValuesRepo.byStation(stationId);
  const filtered =
    event.reportBase === 'SummaryInventory' ? rows.filter((r) => r.mutability !== 'ReadOnly') : rows;
  const reportData = filtered.map((r) => variableToReportData(r));
  await emitNotifyReports(adapter, event.requestId, reportData, rows);
}

export async function handleGetReport(
  event: CsmsEvent<'GetReport'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // Session 19b — Device Model inventory with an optional componentVariable[]
  // filter. v2.0.1-only.
  if (!(adapter instanceof OcppV201Adapter)) {
    dispatcher.replyError(messageId, 'NotImplemented', 'GetReport is v2.0.1-only', {});
    return;
  }
  const rows = await varValuesRepo.byStation(stationId);
  const matched =
    event.filter.length === 0
      ? rows
      : rows.filter((r) =>
          event.filter.some(
            (f) =>
              f.component === r.component && (f.variable === undefined || f.variable === r.variable),
          ),
        );
  // GenericDeviceModelStatus { Accepted, Rejected, NotSupported, EmptyResultSet }.
  if (matched.length === 0) {
    dispatcher.reply(messageId, { status: 'EmptyResultSet' });
    return;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
  const reportData = matched.map((r) => variableToReportData(r));
  await emitNotifyReports(adapter, event.requestId, reportData, rows);
}
